"use client";

import React, { useState } from "react";
import { Button, Text } from "ui-components";
import { v4 as uuidv4 } from "uuid";

import { CookieKeys } from "@/enums/CookieKeys.enum";
import { useCookies } from "@/hooks/useCookies";
import EmailCollection from "@/src/app/[locale]/(public)/jobs/[id]/Components/EmailCollection";
import { countries } from "@/src/constants/conutries";
import {
  useAnalyticsCreate,
  CreateAnalyticsDtoAction,
  useAnalyticsUpdate,
  UpdateAnalyticsDtoEntityType,
  UpdateAnalyticsDtoType,
  useAppGetGeneralSettings,
  JobResponseEntity,
  brevoCreateUserActionLog,
  CreateUserActionLogDtoActionType,
  CreateUserActionLogDtoEntityType,
  CreateUserActionLogDtoUserType,
} from "@/src/hooks/api/generated";
import { useAuth } from "@/src/hooks/useAuth";
import { logBrazeCustomEvent } from "@/utils/brazeClient";

export interface IJobsDetailActionProps {
  id: number;
  jobDetails?: JobResponseEntity;
  title?: string;
  slug?: string;
  applyUrl?: string;
  isApplyWithEmail?: boolean;
  applyEmail?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "xxl" | null | undefined;
  className?: string;
  souceInternalId?: string;
}

export default function JobsDetailAction({
  id,
  jobDetails,
  applyUrl,
  title,
  slug,
  isApplyWithEmail,
  applyEmail,
  size,
  className,
  souceInternalId,
}: IJobsDetailActionProps): JSX.Element {
  const { authData, isAuthenticated } = useAuth();
  const { mutate: analyticsCreate } = useAnalyticsCreate();
  const { mutate: analyticsUpdate } = useAnalyticsUpdate();
  const { getCookie, setCookie } = useCookies();
  const [isShowModal, setIsShowModal] = useState(false);
  const { data: emailCaptureSettings, status: emailCaptureSettingsStatus } =
    useAppGetGeneralSettings("job_details_email_capture");

  const searchParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );

  const shouldShowPopupBySource = (): boolean => {
    if (!emailCaptureSettings) {
      return false;
    }

    // emailCaptureSettings is now a single GeneralSettingsEntity for job_details_email_capture
    if (emailCaptureSettings.event_value !== "true") {
      return false;
    }

    let sourcesConfig: Array<{
      source: string;
      enabled: boolean;
      intensity: number;
    }> = [];

    if (Array.isArray(emailCaptureSettings.json)) {
      sourcesConfig = emailCaptureSettings.json as Array<{
        source: string;
        enabled: boolean;
        intensity: number;
      }>;
    }

    const currentSourceRaw = searchParams.get("utm_source");
    const currentSource = currentSourceRaw
      ? currentSourceRaw.toLowerCase()
      : "organic (none)";

    const sourceRule = sourcesConfig.find(
      (s: { source: string; enabled: boolean; intensity: number }) =>
        s.source.toLowerCase() === currentSource
    );

    if (sourceRule) {
      if (!sourceRule.enabled || sourceRule.intensity <= 0) {
        return false;
      }
      if (sourceRule.intensity >= 100) {
        return true;
      }

      const cacheKey = `emailCaptureCalculated_${currentSource}_${sourceRule.intensity}`;
      const storedResult = sessionStorage.getItem(cacheKey);
      if (storedResult !== null) {
        return storedResult === "true";
      }

      const randomValue = Math.random() * 100;
      const willShow = randomValue <= sourceRule.intensity;
      sessionStorage.setItem(cacheKey, willShow.toString());
      return willShow;
    }

    // Fallback: master is on, source not listed -> 100% intensity
    return true;
  };

  const handleApplyClick = (): void => {
    // ATTB: treat Apply click as the “conversion” event
    injectAttbScript();
    if (
      emailCaptureSettingsStatus === "success" &&
      !getCookie(CookieKeys.EmailCollect) &&
      !getCookie(CookieKeys.EmailCollectClose) &&
      !getCookie(CookieKeys.InlineEmailCollectSubmitted) &&
      !authData &&
      shouldShowPopupBySource()
    ) {
      jobTracking("popup");
      setIsShowModal(true);
    } else {
      jobTracking("click");
      if (jobDetails) {
        logBrazeCustomEvent("JOB_APPLIED", {
          id: jobDetails.id,
          title: jobDetails.title,
          org_id: jobDetails.organization.id,
          org_name: jobDetails.organization.name,
          location: `${jobDetails.locations.location}, ${
            countries[jobDetails.locations.country]
          }`,
          type: jobDetails.type,
          industry: jobDetails.industry.name,
        });
      }
      navigateToJobApplication();
    }
  };

  const jobTracking = (
    action: CreateAnalyticsDtoAction,
    autoRedirect = false
  ): void => {
    let uuid;
    if (!authData) {
      if (!getCookie(CookieKeys.UUID)) {
        uuid = uuidv4();
        setCookie({
          key: CookieKeys.UUID,
          value: uuid,
          maxAge: 30 * 60, // 30 minutes in seconds
        });
      } else {
        uuid = getCookie(CookieKeys.UUID);
      }
    }

    const payload = {
      entity_id: id,
      entity_type: UpdateAnalyticsDtoEntityType.jobs,
      type: UpdateAnalyticsDtoType.job_apply_btn,
      uuid,
      utm_source: searchParams.get("utm_source") ?? undefined,
      utm_medium: searchParams.get("utm_medium") ?? undefined,
      utm_campaign: searchParams.get("utm_campaign") ?? undefined,
      utm_term: searchParams.get("utm_term") ?? undefined,
      utm_content: searchParams.get("utm_content") ?? undefined,
      auto_redirect: autoRedirect,
    };

    const userId = authData?.id || undefined;

    if (action === "click") {
      if (!authData) {
        analyticsUpdate({
          data: { ...payload, action: "click", uuid },
        });
      } else {
        analyticsCreate({
          data: {
            ...payload,
            action: "click",
            userId,
          },
        });
      }
    } else {
      analyticsCreate({
        data: {
          ...payload,
          action: "popup",
          userId,
          uuid,
        },
      });
    }
    const nonMemberUuid: string | undefined = getCookie(
      CookieKeys.NonMemberUUID
    );
    if (isAuthenticated || nonMemberUuid) {
      brevoCreateUserActionLog({
        action_type: CreateUserActionLogDtoActionType.JOB_APPLIED,
        entity_type: CreateUserActionLogDtoEntityType.job,
        entity_id: id,
        uuid: (authData ? authData.uuid : nonMemberUuid) ?? "",
        user_type: authData
          ? CreateUserActionLogDtoUserType.member
          : CreateUserActionLogDtoUserType["non-member"],
        auto_redirect: autoRedirect,
      });
    }
  };

  const injectAttbScript = (): void => {
    const params = new URLSearchParams(window.location.search);
    const attbCid = params.get("attb_cid");
    if (attbCid) {
      fetch(
        `https://conversion.allthetopbananas.com/api/conversion?clickid=${attbCid}`,
        { method: "PUT" }
      );
    }
  };

  const navigateToJobApplication = (): void => {
    if (
      (isApplyWithEmail && applyEmail && souceInternalId === "") ||
      /^mailto:/i.test(applyUrl ?? "")
    ) {
      const subject = title
        ? `Application via myGwork: ${title}`
        : "Job Application via myGwork";
      const useEmailApply =
        isApplyWithEmail && applyEmail && souceInternalId === "";
      const mailTarget = useEmailApply
        ? `mailto:${applyEmail}`
        : applyUrl ?? "";
      const hasSubject = /[?&]subject=/i.test(mailTarget);
      const separator = mailTarget.includes("?") ? "&" : "?";
      window.location.href = hasSubject
        ? mailTarget
        : `${mailTarget}${separator}subject=${encodeURIComponent(subject)}`;
    } else if (applyUrl) {
      window.open(applyUrl, "_blank", "noopener");
    }
  };

  return (
    <div className="flex w-full justify-start">
      {applyUrl && (
        <>
          <EmailCollection
            jobSlug={slug}
            isShowModal={isShowModal}
            setIsShowModal={setIsShowModal}
            handleApplyClick={handleApplyClick}
          />
          <Button
            className={`${className} whitespace-nowrap`}
            size={size}
            onClick={handleApplyClick}
          >
            <Text>Apply Now</Text>
          </Button>
        </>
      )}
    </div>
  );
}
